<template>
  <section
    :class="{
      'location-fetched': geolocationServiceAvailable,
      error: !!error,
    }"
  >
    <base-checkbox
      :model-value="!!currentPoint"
      data-test="geolocation-checkbox"
      @change="checkboxChanged($event)"
    >
      <geolocation-state :machine-states="state?.toStrings() || []" />
    </base-checkbox>
    <p v-if="error" class="error-message">
      {{ errorLocalized }}
    </p>
  </section>
</template>

<script lang="ts">
import { isEqual } from 'lodash-es'
import { interpret } from 'xstate-v4'
import { GeoPoint } from '~/utility/geo/GeoLocation'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import {
  MachineInterpreter,
  MachineState,
  returnGeolocationMachine,
} from '../machine/machine'

@Component({
  emits: ['point-change'],
})
class GeolocationCheckbox extends Vue {
  interpreter: MachineInterpreter | null = null
  state: MachineState | null = null

  get error() {
    return this.state?.context.errorMessage || ''
  }

  get errorLocalized(): string {
    return this.$t(this.error).toString()
  }

  get geolocationServiceAvailable() {
    return this.state?.matches('usingLocation.available') || false
  }

  @Prop()
  currentPoint?: GeoPoint | null

  get point(): GeoPoint | null {
    return this.state?.context.point || null
  }

  @Watch('point')
  onPointChanged(newPoint: GeoPoint, oldPoint: GeoPoint) {
    if (!isEqual(newPoint, oldPoint)) {
      this.$emit('point-change', newPoint)
    }
  }

  @Watch('currentPoint')
  onCurrentPointChanged(newPoint: GeoPoint) {
    if (!newPoint) {
      this.interpreter?.send({ type: 'DONT_USE_LOCATION' })
    }
  }

  mounted() {
    this.interpreter = interpret(returnGeolocationMachine())
      .onTransition((state) => {
        this.state = state
      })
      .start()
  }

  beforeUnmount() {
    if (this && this.interpreter) {
      this.interpreter.stop()
      this.interpreter = null
    }
  }

  // checkbox calls this when changed
  checkboxChanged(use: boolean): void {
    if (use) {
      // starts a request for a new location
      this.interpreter?.send({ type: 'USE_LOCATION' })
    } else {
      // cancels any request and existing point
      this.interpreter?.send({ type: 'DONT_USE_LOCATION' })
      // special case, when it has no point (Because it was SSR, then emit a null)
      if (!this.point) {
        this.$emit('point-change', null)
      }
    }
  }
}

export default toNative(GeolocationCheckbox)
</script>

<style scoped>
section {
  padding: 0 0 0 0.7em;
}

section :deep(label) {
  margin: 0;
  color: var(--colour-dark-blue);
}

.error :deep(label) {
  font-weight: normal;
}

.error-message {
  color: var(--colour-red);
  font-size: var(--small);
  padding-top: 0.5em;
}
</style>
